/**
 * Where each trade on a job has got to — docs/BUSINESS-RULES.md.
 *
 * The checklist carries two steps for the work, "All Work Scheduled" and "All
 * Work Complete", and both are decided by the work orders. That keeps the
 * ticking down to two boxes, but on its own it loses something worth seeing:
 * on a roof + siding + gutters job, which crew is holding the job up.
 *
 * So the detail is read back out of the work orders rather than being typed in
 * again. Roofing, siding and gutters each show their own state, and nobody
 * ticks anything extra to make that happen.
 */

import type { WorkOrder } from "./types.js";

export type WorkState = "not_scheduled" | "scheduled" | "complete";

export interface TradeProgress {
  trade: string;
  state: WorkState;
  /** When it is booked in, if it is. */
  scheduledAt: string | null;
  /** Work orders behind this row — a trade can have more than one. */
  count: number;
  /**
   * The work orders themselves, so a screen can open one.
   *
   * The checklist's two work steps are not ticked by hand: the work orders
   * decide them, and the box that actually moves is "Mark As Complete" inside
   * a work order. Clicking the step used to leave somebody on the estimate
   * page to find that themselves, which reads as a step that does nothing
   * (Jonathon, 2026-09-08). These ids are what lets the click land on the
   * order rather than near it.
   */
  orderIds: string[];
}

type WorkOrderLike = Pick<
  WorkOrder,
  "trades" | "status" | "scheduled_at" | "completed_at"
> & { id?: string | null };

/** Booked in, or further along. A paid work order was certainly scheduled. */
export const isScheduled = (order: WorkOrderLike): boolean =>
  order.scheduled_at != null ||
  ["scheduled", "completed", "paid"].includes(order.status);

/** The crew has finished. Material delivery is tracked on its own step. */
export const isComplete = (order: WorkOrderLike): boolean =>
  order.completed_at != null || ["completed", "paid"].includes(order.status);

const stateOf = (orders: readonly WorkOrderLike[]): WorkState => {
  if (orders.every(isComplete)) return "complete";
  if (orders.every(isScheduled)) return "scheduled";
  return "not_scheduled";
};

/** Work orders with no trade named still have to appear somewhere. */
const UNNAMED = "Other work";

/**
 * The trades on one work order.
 *
 * The column is `text[]`, but rows the prototype wrote are a plain string, and
 * a comma-separated one at that. Both shapes arrive from PostgREST.
 */
export function tradeNames(
  value: string[] | string | null | undefined,
): string[] {
  if (Array.isArray(value)) return value.map((n) => n.trim()).filter(Boolean);
  return (value ?? "")
    .split(",")
    .map((n) => n.trim())
    .filter(Boolean);
}

/**
 * One row per trade on the job, in the order the trades were first seen.
 *
 * A trade is only as far along as its least advanced work order: two siding
 * orders where one is done and one is not means siding is not done.
 */
export function tradeProgress(
  orders: readonly WorkOrderLike[],
): TradeProgress[] {
  const byTrade = new Map<string, WorkOrderLike[]>();

  for (const order of orders) {
    const names = tradeNames(order.trades);
    for (const name of names.length ? names : [UNNAMED]) {
      const list = byTrade.get(name);
      if (list) list.push(order);
      else byTrade.set(name, [order]);
    }
  }

  return [...byTrade.entries()].map(([trade, list]) => ({
    trade,
    state: stateOf(list),
    scheduledAt:
      list
        .map((o) => o.scheduled_at)
        .filter((d): d is string => d != null)
        .sort()[0] ?? null,
    count: list.length,
    orderIds: list
      .map((order) => order.id)
      .filter((id): id is string => Boolean(id)),
  }));
}

/**
 * Is every piece of work on this job scheduled / complete?
 *
 * The same two questions the database trigger answers, kept here so the UI can
 * explain a step without waiting for a round trip. A job with no work orders is
 * neither: nothing has been planned yet, and "all zero of them are done" must
 * never read as finished.
 */
export function workSummary(orders: readonly WorkOrderLike[]): {
  scheduled: boolean;
  complete: boolean;
  total: number;
} {
  return {
    scheduled: orders.length > 0 && orders.every(isScheduled),
    complete: orders.length > 0 && orders.every(isComplete),
    total: orders.length,
  };
}
